import { api, apiErrorMessage, useAuthStore } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Input,
    Label,
} from '@bemor/web-kit/ui';
import { useEffect, useState } from 'react';

import type { Tenant } from '@/api/types';
import { P } from '@/lib/permissions';
import { useWrite } from '@/lib/queries';

/**
 * BM-123: the tenant's assistant budget (tokens per month; empty = the platform default). The kill switch is the
 * platform's `ai.enabled` flag on the flags page; the tenant's own usage shows on its board (`/staff/ai/usage`).
 */
export function AiSection({ tenant }: { tenant: Tenant }) {
    const can = useAuthStore((s) => s.can);
    const [budget, setBudget] = useState(
        tenant.ai_budget_tokens == null ? '' : String(tenant.ai_budget_tokens),
    );
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        setBudget(
            tenant.ai_budget_tokens == null
                ? ''
                : String(tenant.ai_budget_tokens),
        );
    }, [tenant.ai_budget_tokens]);
    const save = useWrite(
        async (value: string) =>
            (
                await api().patch<Tenant>(`/staff/tenants/${tenant.id}`, {
                    ai_budget_tokens: value === '' ? null : Number(value),
                    version: tenant.version,
                })
            ).data,
        ['/staff/tenants'],
    );
    const dirty =
        budget !==
        (tenant.ai_budget_tokens == null
            ? ''
            : String(tenant.ai_budget_tokens));
    return (
        <Card data-testid="tenant-ai">
            <CardHeader>
                <CardTitle className="text-sm font-medium">Assistant</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-muted-foreground text-sm">
                    The assistant and the MCP tools of this tenant's boards.
                    Switch it off platform-wide with the <code>ai.enabled</code>{' '}
                    kill switch on the flags page. The budget is tokens per
                    month; at 80 % an alert warns, at 100 % the assistant
                    refuses until next month.
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                        <Label htmlFor="ai-budget">Monthly token budget</Label>
                        <Input
                            id="ai-budget"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="platform default"
                            value={budget}
                            disabled={!can(P.tenantsManage)}
                            onChange={(e) => setBudget(e.target.value)}
                        />
                    </div>
                </div>
                {error ? (
                    <p role="alert" className="text-destructive text-sm">
                        {error}
                    </p>
                ) : null}
                {can(P.tenantsManage) ? (
                    <Button
                        size="sm"
                        disabled={!dirty || save.isPending}
                        onClick={() => {
                            setError(null);
                            save.mutate(budget, {
                                onError: (e) => setError(apiErrorMessage(e)),
                            });
                        }}
                    >
                        Save budget
                    </Button>
                ) : null}
            </CardContent>
        </Card>
    );
}
